import React, { useState, useEffect, useCallback } from 'react';
import EmailDeliverability from '../components/EmailDeliverability';
import { Search, TrendingUp, TrendingDown, Minus, Plus, X, ChevronDown, ChevronRight, Eye, Bot, Target, Lightbulb, Check, ExternalLink, RefreshCw, ArrowUp, ArrowDown, Info, Mail, AlertTriangle } from 'lucide-react';
import { sessionHeaders } from '../services/auth';

const NEON = '/.netlify/functions/neon';
const GSC = '/.netlify/functions/gsc-proxy';
function neonQuery(query, params = {}) {
  return fetch(NEON, { method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() }, body: JSON.stringify({ query, params }) }).then(r => r.json());
}
function gscQuery(action, params = {}) {
  return fetch(GSC, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, params }) }).then(r => r.json());
}

const CATEGORIES = ['general', 'product', 'brand', 'competitor', 'local', 'seasonal'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];
const PRIORITY_COLORS = {
  critical: { bg: '#fde8e8', color: '#c0392b' },
  high:     { bg: '#fff3e0', color: '#e65100' },
  medium:   { bg: '#e8f0fe', color: '#1a73e8' },
  low:      { bg: '#f5f5f5', color: '#5f6368' },
};

function PositionBadge({ current, previous }) {
  if (current == null) return <span style={{ color: '#9aa0a6', fontSize: 12 }}>--</span>;
  const pos = parseFloat(current);
  const prev = previous != null ? parseFloat(previous) : null;
  const diff = prev != null ? prev - pos : null; // positive = improved
  const color = pos <= 3 ? '#1e8e3e' : pos <= 10 ? '#1a73e8' : pos <= 20 ? '#f9ab00' : '#d93025';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontWeight: 700, fontSize: 16, color }}>{pos.toFixed(1)}</span>
      {diff != null && diff !== 0 && (
        <span style={{
          display: 'flex', alignItems: 'center', gap: 2,
          fontSize: 11, fontWeight: 600,
          color: diff > 0 ? '#1e8e3e' : '#d93025',
        }}>
          {diff > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          {Math.abs(diff).toFixed(1)}
        </span>
      )}
    </div>
  );
}

function PriorityBadge({ priority }) {
  const s = PRIORITY_COLORS[priority] || PRIORITY_COLORS.medium;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
      background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {priority}
    </span>
  );
}

function AeoBadge({ present, mentionsUs }) {
  if (!present) return <span style={{ fontSize: 11, color: '#9aa0a6' }}>No AI Overview</span>;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
      background: mentionsUs ? '#e6f9f0' : '#fff3e0',
      color: mentionsUs ? '#1e8e3e' : '#e65100',
      textTransform: 'uppercase',
    }}>
      {mentionsUs ? 'Mentioned' : 'Not Mentioned'}
    </span>
  );
}

export default function Seo() {
  const [keywords, setKeywords] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('keywords');
  const [showAdd, setShowAdd] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newUrl, setNewUrl] = useState('');
  const [addingKeyword, setAddingKeyword] = useState(false);
  const [expandedKeyword, setExpandedKeyword] = useState(null);
  const [rankingHistory, setRankingHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Manual ranking entry
  const [manualPos, setManualPos] = useState('');
  const [manualClicks, setManualClicks] = useState('');
  const [manualImpressions, setManualImpressions] = useState('');
  // AEO manual update
  const [aeoForm, setAeoForm] = useState({});
  // Recommendation form
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [showRecForm, setShowRecForm] = useState(false);
  const [recForm, setRecForm] = useState({ rec_type: 'content', title: '', description: '', priority: 'medium', keyword_id: null });
  // GSC status
  const [gscConnected, setGscConnected] = useState(false);
  const [gscSyncing, setGscSyncing] = useState(false);
  const [gscSyncResult, setGscSyncResult] = useState(null);
  const [sectionsOpen, setSectionsOpen] = useState({ kpis: true, keywords: true, aeo: true, recs: true });
  const [initialLoad, setInitialLoad] = useState(true);

  const toggleSection = (s) => setSectionsOpen(prev => ({ ...prev, [s]: !prev[s] }));

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Init tables on first load
      await neonQuery('seo_init');
      const [kwRes, kpiRes, recRes, gscRes] = await Promise.all([
        neonQuery('seo_keywords_list'),
        neonQuery('seo_kpis'),
        neonQuery('seo_recommendations_list'),
        gscQuery('status').catch(() => ({ connected: false })),
      ]);
      if (kwRes.error) throw new Error(kwRes.error);
      setKeywords(kwRes.rows || []);
      setKpis(kpiRes.rows?.[0] || {});
      setRecommendations(recRes.rows || []);
      setGscConnected(gscRes.connected === true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const syncFromGsc = async () => {
    setGscSyncing(true);
    setGscSyncResult(null);
    try {
      const res = await gscQuery('sync');
      if (res.error) throw new Error(res.error);
      setGscSyncResult(`Imported ${res.imported} keywords, synced ${res.synced} data points from GSC`);
      await loadData();
    } catch (e) {
      setGscSyncResult(`Sync error: ${e.message}`);
    } finally {
      setGscSyncing(false);
    }
  };

  const addKeyword = async () => {
    if (!newKeyword.trim()) return;
    setAddingKeyword(true);
    try {
      await neonQuery('seo_keyword_add', { keyword: newKeyword.trim(), category: newCategory, target_url: newUrl || null });
      setNewKeyword('');
      setNewUrl('');
      setShowAdd(false);
      await loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingKeyword(false);
    }
  };

  const removeKeyword = async (id) => {
    await neonQuery('seo_keyword_remove', { id });
    await loadData();
  };

  const loadHistory = async (keywordId) => {
    if (expandedKeyword === keywordId) { setExpandedKeyword(null); return; }
    setExpandedKeyword(keywordId);
    setHistoryLoading(true);
    try {
      const res = await neonQuery('seo_ranking_history', { keyword_id: keywordId });
      setRankingHistory(res.rows || []);
    } finally {
      setHistoryLoading(false);
    }
  };

  const saveManualRanking = async (keywordId) => {
    if (!manualPos) return;
    const ctr = manualImpressions && parseInt(manualImpressions) > 0
      ? ((parseInt(manualClicks || 0) / parseInt(manualImpressions)) * 100)
      : 0;
    await neonQuery('seo_ranking_add', {
      keyword_id: keywordId,
      position: parseFloat(manualPos),
      clicks: parseInt(manualClicks || 0),
      impressions: parseInt(manualImpressions || 0),
      ctr: ctr.toFixed(2),
      source: 'manual',
    });
    setManualPos('');
    setManualClicks('');
    setManualImpressions('');
    await loadData();
    await loadHistory(keywordId);
    setExpandedKeyword(keywordId); // keep expanded
  };

  const saveAeoCheck = async (keywordId) => {
    const f = aeoForm[keywordId] || {};
    await neonQuery('seo_aeo_update', {
      keyword_id: keywordId,
      ai_overview_present: f.ai_overview_present || false,
      ai_overview_mentions_us: f.ai_overview_mentions_us || false,
      ai_overview_position: f.ai_overview_position || null,
      perplexity_mentions: f.perplexity_mentions || false,
      chatgpt_mentions: f.chatgpt_mentions || false,
      notes: f.notes || null,
    });
    await loadData();
  };

  const addRecommendation = async () => {
    if (!recForm.title.trim()) return;
    await neonQuery('seo_recommendation_add', recForm);
    setRecForm({ rec_type: 'content', title: '', description: '', priority: 'medium', keyword_id: null });
    setShowRecForm(false);
    await loadData();
  };

  const updateRecStatus = async (id, status) => {
    await neonQuery('seo_recommendation_update', { id, status });
    // Update locally without full reload to avoid scroll reset
    setRecommendations(prev => prev.map(r =>
      r.id === id ? { ...r, status, completed_at: status === 'completed' ? new Date().toISOString() : r.completed_at } : r
    ).filter(r => r.status !== 'dismissed'));
  };

  if (loading && initialLoad) return <div className="page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--navy)' }} /></div>;

  const kpiCards = [
    { label: 'Tracked Keywords', value: kpis?.tracked_keywords || 0, icon: Target, color: 'var(--navy)' },
    { label: 'Avg Position', value: kpis?.avg_position || '--', icon: TrendingUp, color: '#1a73e8' },
    { label: 'Page 1 Keywords', value: kpis?.page1_count || 0, icon: Eye, color: '#1e8e3e', sub: `${kpis?.top3_count || 0} in Top 3` },
    { label: 'Clicks', value: (kpis?.total_clicks || 0).toLocaleString(), icon: Search, color: 'var(--orange)', sub: kpis?.snapshot_date ? new Date(kpis.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '' },
    { label: 'Impressions', value: (kpis?.total_impressions || 0).toLocaleString(), icon: Eye, color: '#7c3aed', sub: kpis?.snapshot_date ? new Date(kpis.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '' },
    { label: 'Avg CTR', value: `${kpis?.avg_ctr || 0}%`, icon: TrendingUp, color: '#1e8e3e' },
    { label: 'AEO Mentions', value: kpis?.aeo_mentions || 0, icon: Bot, color: '#e65100', sub: 'AI Overview' },
  ];

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>SEO & AEO Dashboard</h1>
          <p style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 2 }}>Track keyword rankings, AI answer visibility, and optimization opportunities</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, color: gscConnected ? '#1e8e3e' : '#f9ab00' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: gscConnected ? '#1e8e3e' : '#f9ab00', display: 'inline-block' }} />
            GSC {gscConnected ? 'Connected' : 'Not Connected'}
          </span>
          {gscConnected && (
            <button className="btn btn-primary" onClick={syncFromGsc} disabled={gscSyncing} style={{ fontSize: 12, padding: '6px 12px' }}>
              <RefreshCw size={13} style={{ marginRight: 4, ...(gscSyncing ? { animation: 'spin 1s linear infinite' } : {}) }} />
              {gscSyncing ? 'Syncing...' : 'Sync GSC'}
            </button>
          )}
          <button className="btn btn-outline" onClick={loadData} style={{ fontSize: 12, padding: '6px 12px' }}>
            <RefreshCw size={13} style={{ marginRight: 4 }} /> Refresh
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fde8e8', color: '#c0392b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>{error}</div>}

      {/* KPI Cards */}
      <div className="collapsible-header" onClick={() => toggleSection('kpis')} style={{ marginBottom: 8 }}>
        {sectionsOpen.kpis ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>Key Metrics</span>
      </div>
      {sectionsOpen.kpis && (
        <div className="seo-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
          {kpiCards.map((c, i) => (
            <div key={i} className="kpi-card" style={{ textAlign: 'center', position: 'relative' }}>
              <c.icon size={18} style={{ color: c.color, marginBottom: 4 }} />
              <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-700)', fontWeight: 500 }}>{c.label}</div>
              {c.sub && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{c.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--gray-200)', marginBottom: 16 }}>
        {[
          { id: 'keywords', label: 'Keyword Rankings', icon: Search },
          { id: 'aeo', label: 'AEO Tracker', icon: Bot },
          { id: 'recommendations', label: 'AI Recommendations', icon: Lightbulb },
          { id: 'reputation', label: 'Email Reputation', icon: Mail },
          { id: 'setup', label: 'Setup', icon: Target },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 12, color: tab === t.id ? 'var(--orange)' : 'var(--gray-700)',
            borderBottom: tab === t.id ? '2px solid var(--orange)' : '2px solid transparent',
            marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6,
            fontFamily: 'inherit',
          }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Keywords Tab */}
      {tab === 'keywords' && (
        <div>
          {gscSyncResult && (
            <div style={{ background: '#e6f9f0', color: '#1a8a4a', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {gscSyncResult}
              <button onClick={() => setGscSyncResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={14} /></button>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--gray-700)' }}>{keywords.length} tracked keyword{keywords.length !== 1 ? 's' : ''}</span>
            <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)} style={{ fontSize: 12, padding: '6px 12px' }}>
              <Plus size={13} style={{ marginRight: 4 }} /> Add Keyword
            </button>
          </div>


          {showAdd && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-body" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: 200 }}>
                  <label className="form-label">Keyword</label>
                  <input className="form-input" value={newKeyword} onChange={e => setNewKeyword(e.target.value)}
                    placeholder="e.g., buy live crabs online" onKeyDown={e => e.key === 'Enter' && addKeyword()} />
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <label className="form-label">Category</label>
                  <select className="form-select" value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div style={{ flex: 2, minWidth: 200 }}>
                  <label className="form-label">Target URL (optional)</label>
                  <input className="form-input" value={newUrl} onChange={e => setNewUrl(e.target.value)}
                    placeholder="https://crabplace.com/..." />
                </div>
                <button className="btn btn-primary" onClick={addKeyword} disabled={addingKeyword} style={{ fontSize: 12 }}>
                  {addingKeyword ? 'Adding...' : 'Add'}
                </button>
                <button className="btn btn-ghost" onClick={() => setShowAdd(false)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}

          {keywords.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 40 }}>
              <Search size={40} style={{ color: 'var(--gray-300)', marginBottom: 12 }} />
              <p style={{ color: 'var(--gray-700)', fontSize: 14, fontWeight: 600 }}>No keywords tracked yet</p>
              <p style={{ color: 'var(--gray-500)', fontSize: 12, marginTop: 4 }}>Click "Sync GSC" to auto-import all your Google Search Console queries, or add keywords manually.</p>
            </div>
          ) : (
            <div className="card">
              <table className="product-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>Keyword</th>
                    <th>Category</th>
                    <th style={{ textAlign: 'center' }}>Position <span title={"Your average Google ranking for this keyword. Example: 8.1 means you show up around the 8th result. Positions 1–10 = page 1, 11–20 = page 2, etc. It's a decimal because Google averages your rank across all searches — sometimes you're 7th, sometimes 9th. The colored arrow shows change since last sync: green ↓ = you moved up closer to #1 (good), red ↑ = you dropped (bad)."} style={{ cursor: 'pointer', verticalAlign: 'middle' }}><Info size={13} style={{ opacity: 0.5 }} /></span></th>
                    <th style={{ textAlign: 'right' }}>Clicks</th>
                    <th style={{ textAlign: 'right' }}>Impressions</th>
                    <th style={{ textAlign: 'right' }}>CTR</th>
                    <th style={{ textAlign: 'center' }}>AEO</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map(kw => (
                    <React.Fragment key={kw.id}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => loadHistory(kw.id)}>
                        <td>{expandedKeyword === kw.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                        <td>
                          <span style={{ fontWeight: 600 }}>{kw.keyword}</span>
                          {kw.target_url && (
                            <a href={kw.target_url} target="_blank" rel="noopener noreferrer"
                              style={{ marginLeft: 6, color: 'var(--gray-500)' }} onClick={e => e.stopPropagation()}>
                              <ExternalLink size={11} />
                            </a>
                          )}
                        </td>
                        <td>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8, background: 'var(--gray-100)', color: 'var(--gray-700)' }}>
                            {kw.category}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <PositionBadge current={kw.current_position} previous={kw.prev_position} />
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{kw.latest_clicks ?? '--'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{kw.latest_impressions ?? '--'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{kw.latest_ctr != null ? `${parseFloat(kw.latest_ctr).toFixed(1)}%` : '--'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <AeoBadge present={kw.ai_overview_present} mentionsUs={kw.ai_overview_mentions_us} />
                        </td>
                        <td>
                          <button className="btn btn-ghost" onClick={e => { e.stopPropagation(); removeKeyword(kw.id); }}
                            style={{ padding: 4, minWidth: 0 }} title="Remove">
                            <X size={14} style={{ color: 'var(--gray-500)' }} />
                          </button>
                        </td>
                      </tr>

                      {expandedKeyword === kw.id && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0, background: 'var(--gray-50)' }}>
                            <div style={{ padding: 16 }}>
                              {/* Manual ranking entry */}
                              <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div>
                                  <label className="form-label" style={{ fontSize: 11 }}>Position</label>
                                  <input className="form-input" style={{ width: 80 }} type="number" step="0.1" value={manualPos}
                                    onChange={e => setManualPos(e.target.value)} placeholder="e.g., 8.3" />
                                </div>
                                <div>
                                  <label className="form-label" style={{ fontSize: 11 }}>Clicks</label>
                                  <input className="form-input" style={{ width: 80 }} type="number" value={manualClicks}
                                    onChange={e => setManualClicks(e.target.value)} placeholder="0" />
                                </div>
                                <div>
                                  <label className="form-label" style={{ fontSize: 11 }}>Impressions</label>
                                  <input className="form-input" style={{ width: 100 }} type="number" value={manualImpressions}
                                    onChange={e => setManualImpressions(e.target.value)} placeholder="0" />
                                </div>
                                <button className="btn btn-primary" onClick={() => saveManualRanking(kw.id)} style={{ fontSize: 11 }}>
                                  Save Today's Data
                                </button>
                              </div>

                              {/* History table */}
                              {historyLoading ? (
                                <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading history...</p>
                              ) : rankingHistory.length === 0 ? (
                                <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>No ranking history yet. Add data from GSC or manually above.</p>
                              ) : (
                                <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th style={{ textAlign: 'center' }}>Position</th>
                                      <th style={{ textAlign: 'right' }}>Clicks</th>
                                      <th style={{ textAlign: 'right' }}>Impressions</th>
                                      <th style={{ textAlign: 'right' }}>CTR</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rankingHistory.map((r, i) => {
                                      const prev = rankingHistory[i + 1]; // older entry
                                      return (
                                        <tr key={i}>
                                          <td>{new Date(r.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                                          <td style={{ textAlign: 'center' }}>
                                            <PositionBadge current={r.position} previous={prev?.position} />
                                          </td>
                                          <td style={{ textAlign: 'right' }}>{r.clicks}</td>
                                          <td style={{ textAlign: 'right' }}>{r.impressions}</td>
                                          <td style={{ textAlign: 'right' }}>{parseFloat(r.ctr).toFixed(1)}%</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* AEO Tab */}
      {tab === 'aeo' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Answer Engine Optimization</span>
            </div>
            <div className="card-body">
              <p style={{ fontSize: 12, color: 'var(--gray-700)', marginBottom: 12 }}>
                AEO tracks whether CrabPlace appears in AI-generated answers (Google AI Overviews, ChatGPT, Perplexity).
                For each tracked keyword, search it on Google and in AI tools, then record whether CrabPlace is mentioned.
              </p>

              {keywords.length === 0 ? (
                <p style={{ color: 'var(--gray-500)', fontSize: 12 }}>Add keywords first in the Keywords tab.</p>
              ) : (
                <table className="product-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Keyword</th>
                      <th style={{ textAlign: 'center' }}>AI Overview?</th>
                      <th style={{ textAlign: 'center' }}>Mentions Us?</th>
                      <th style={{ textAlign: 'center' }}>Position in AI</th>
                      <th style={{ textAlign: 'center' }}>Perplexity</th>
                      <th style={{ textAlign: 'center' }}>ChatGPT</th>
                      <th>Notes</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map(kw => {
                      const f = aeoForm[kw.id] || {
                        ai_overview_present: kw.ai_overview_present || false,
                        ai_overview_mentions_us: kw.ai_overview_mentions_us || false,
                        ai_overview_position: null,
                        perplexity_mentions: false,
                        chatgpt_mentions: false,
                        notes: '',
                      };
                      const updateField = (field, val) => setAeoForm(prev => ({ ...prev, [kw.id]: { ...f, [field]: val } }));
                      return (
                        <tr key={kw.id}>
                          <td style={{ fontWeight: 600 }}>{kw.keyword}</td>
                          <td style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={f.ai_overview_present} onChange={e => updateField('ai_overview_present', e.target.checked)} />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={f.ai_overview_mentions_us} onChange={e => updateField('ai_overview_mentions_us', e.target.checked)}
                              disabled={!f.ai_overview_present} />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input className="form-input" type="number" style={{ width: 50, textAlign: 'center', fontSize: 11 }}
                              value={f.ai_overview_position || ''} onChange={e => updateField('ai_overview_position', e.target.value ? parseInt(e.target.value) : null)}
                              disabled={!f.ai_overview_present} placeholder="--" />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={f.perplexity_mentions} onChange={e => updateField('perplexity_mentions', e.target.checked)} />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={f.chatgpt_mentions} onChange={e => updateField('chatgpt_mentions', e.target.checked)} />
                          </td>
                          <td>
                            <input className="form-input" style={{ fontSize: 11 }} value={f.notes || ''} onChange={e => updateField('notes', e.target.value)}
                              placeholder="Notes..." />
                          </td>
                          <td>
                            <button className="btn btn-primary" onClick={() => saveAeoCheck(kw.id)} style={{ fontSize: 10, padding: '4px 8px' }}>Save</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* AEO Strategy Tips */}
          <div className="card">
            <div className="card-header">AEO Strategy Guide</div>
            <div className="card-body" style={{ fontSize: 12, lineHeight: 1.8 }}>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>How to improve AI answer visibility:</p>
              <ul style={{ paddingLeft: 20, color: 'var(--gray-700)' }}>
                <li><strong>Structured data (Schema.org)</strong> — Add Product, FAQ, HowTo, and LocalBusiness schema to crabplace.com pages</li>
                <li><strong>FAQ content</strong> — Create Q&A content that directly answers common queries ("How to ship live crabs", "Best Maryland blue crabs online")</li>
                <li><strong>Authority signals</strong> — Get cited by food blogs, seafood guides, and review sites that AI models train on</li>
                <li><strong>Concise, factual answers</strong> — AI overviews pull from content that directly answers questions in 2-3 sentences</li>
                <li><strong>Brand mentions</strong> — Ensure "CrabPlace" or "CrabPlace.com" appears naturally near your target answer content</li>
                <li><strong>Freshness</strong> — Keep content updated (seasonal pages, pricing, availability) so AI models see current data</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations Tab */}
      {tab === 'recommendations' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--gray-700)' }}>
              {recommendations.filter(r => r.status === 'open').length} open recommendation{recommendations.filter(r => r.status === 'open').length !== 1 ? 's' : ''}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary" onClick={async () => {
                setAnalyzing(true); setAnalyzeResult(null);
                try {
                  const res = await gscQuery('analyze');
                  if (res.error) throw new Error(res.error);
                  setAnalyzeResult(`Generated ${res.added} new recommendation${res.added !== 1 ? 's' : ''}`);
                  await loadData();
                } catch (e) { setAnalyzeResult(`Error: ${e.message}`); }
                finally { setAnalyzing(false); }
              }} disabled={analyzing} style={{ fontSize: 12, padding: '6px 12px' }}>
                <Lightbulb size={13} style={{ marginRight: 4, ...(analyzing ? { animation: 'spin 1s linear infinite' } : {}) }} />
                {analyzing ? 'Analyzing...' : 'Analyze & Generate'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowRecForm(!showRecForm)} style={{ fontSize: 12, padding: '6px 12px' }}>
                <Plus size={13} style={{ marginRight: 4 }} /> Add Manual
              </button>
            </div>
          </div>

          {analyzeResult && (
            <div style={{ background: '#e6f9f0', color: '#1a8a4a', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {analyzeResult}
              <button onClick={() => setAnalyzeResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={14} /></button>
            </div>
          )}

          {showRecForm && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <div style={{ flex: 2, minWidth: 200 }}>
                    <label className="form-label">Title</label>
                    <input className="form-input" value={recForm.title} onChange={e => setRecForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Add FAQ schema to blue crab product pages" />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label className="form-label">Type</label>
                    <select className="form-select" value={recForm.rec_type} onChange={e => setRecForm(prev => ({ ...prev, rec_type: e.target.value }))}>
                      <option value="content">Content</option>
                      <option value="technical">Technical SEO</option>
                      <option value="backlinks">Backlinks</option>
                      <option value="schema">Schema / Structured Data</option>
                      <option value="aeo">AEO</option>
                      <option value="local">Local SEO</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <label className="form-label">Priority</label>
                    <select className="form-select" value={recForm.priority} onChange={e => setRecForm(prev => ({ ...prev, priority: e.target.value }))}>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label className="form-label">Related Keyword</label>
                    <select className="form-select" value={recForm.keyword_id || ''} onChange={e => setRecForm(prev => ({ ...prev, keyword_id: e.target.value || null }))}>
                      <option value="">None</option>
                      {keywords.map(k => <option key={k.id} value={k.id}>{k.keyword}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" rows={3} value={recForm.description}
                    onChange={e => setRecForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Detailed explanation of what to do and why..." />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" onClick={addRecommendation} style={{ fontSize: 12 }}>Save</button>
                  <button className="btn btn-ghost" onClick={() => setShowRecForm(false)} style={{ fontSize: 12 }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {recommendations.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 40 }}>
              <Lightbulb size={40} style={{ color: 'var(--gray-300)', marginBottom: 12 }} />
              <p style={{ color: 'var(--gray-700)', fontSize: 14, fontWeight: 600 }}>No recommendations yet</p>
              <p style={{ color: 'var(--gray-500)', fontSize: 12, marginTop: 4 }}>
                Add recommendations manually, or connect GSC to let Claude analyze your data and suggest improvements.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recommendations.map(rec => (
                <div key={rec.id} className="card" style={{
                  opacity: rec.status === 'completed' ? 0.5 : 1,
                  borderLeft: rec.status === 'in_progress' ? '3px solid var(--orange)' : rec.status === 'completed' ? '3px solid #1e8e3e' : 'none',
                }}>
                  <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <PriorityBadge priority={rec.priority} />
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8, background: 'var(--gray-100)', color: 'var(--gray-700)' }}>
                          {rec.rec_type}
                        </span>
                        {rec.status === 'in_progress' && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 8, background: '#fff3e0', color: 'var(--orange)' }}>
                            IN PROGRESS
                          </span>
                        )}
                        {rec.status === 'completed' && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 8, background: '#e6f9f0', color: '#1e8e3e' }}>
                            DONE
                          </span>
                        )}
                        {rec.keyword && (
                          <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                            <Search size={10} style={{ marginRight: 2 }} />{rec.keyword}
                          </span>
                        )}
                      </div>
                      <p style={{ fontWeight: 600, fontSize: 13, margin: '4px 0' }}>{rec.title}</p>
                      {rec.description && <p style={{ fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.6 }}>{rec.description}</p>}
                      {rec.completed_at && rec.status === 'completed' && (
                        <p style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
                          Completed {new Date(rec.completed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {rec.status === 'open' && (
                        <>
                          <button className="btn btn-outline" onClick={() => updateRecStatus(rec.id, 'in_progress')}
                            style={{ fontSize: 10, padding: '4px 8px' }}>Start</button>
                          <button className="btn btn-ghost" onClick={() => updateRecStatus(rec.id, 'completed')}
                            style={{ fontSize: 10, padding: '4px 8px', color: '#1e8e3e' }}>
                            <Check size={12} /> Done
                          </button>
                        </>
                      )}
                      {rec.status === 'in_progress' && (
                        <button className="btn btn-primary" onClick={() => updateRecStatus(rec.id, 'completed')}
                          style={{ fontSize: 10, padding: '4px 8px', background: '#1e8e3e', borderColor: '#1e8e3e' }}>
                          <Check size={12} style={{ marginRight: 3 }} /> Mark Done
                        </button>
                      )}
                      <button className="btn btn-ghost" onClick={() => updateRecStatus(rec.id, 'dismissed')}
                        style={{ fontSize: 10, padding: '4px 8px', color: 'var(--gray-500)' }} title="Dismiss">
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Email Reputation Tab: Google Postmaster v2 + per-rep reply rates */}
      {tab === 'reputation' && <EmailDeliverability />}

      {/* Setup Tab */}
      {tab === 'setup' && (
        <div>
          {/* GSC Connection */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">Google Search Console Connection</div>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: gscConnected ? '#1e8e3e' : '#d93025',
                  display: 'inline-block',
                }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: gscConnected ? '#1e8e3e' : '#d93025' }}>
                  {gscConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--gray-700)' }}>
                <p style={{ fontWeight: 600, marginBottom: 8 }}>To connect Google Search Console:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>Go to <strong>Google Cloud Console</strong> and create a service account</li>
                  <li>Enable the <strong>Search Console API</strong></li>
                  <li>Download the service account JSON key file</li>
                  <li>In Google Search Console, add the service account email as a <strong>Full user</strong> for crabplace.com</li>
                  <li>Add the JSON credentials to the Netlify environment variable <code>GSC_CREDENTIALS_JSON</code></li>
                  <li>Set <code>GSC_SITE_URL</code> to <code>sc-domain:crabplace.com</code> (or the full URL property)</li>
                </ol>
                <p style={{ marginTop: 12, color: 'var(--gray-500)' }}>
                  Once connected, ranking data will sync daily and appear automatically in the Keywords tab.
                  Until then, you can enter rankings manually.
                </p>
              </div>
            </div>
          </div>

          {/* Seed Keywords */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">Suggested Starter Keywords</div>
            <div className="card-body">
              <p style={{ fontSize: 12, color: 'var(--gray-700)', marginBottom: 12 }}>
                Click any keyword below to add it to your tracker. These are high-value keywords for a seafood e-commerce business.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  { kw: 'buy live crabs online', cat: 'product' },
                  { kw: 'maryland blue crabs delivery', cat: 'product' },
                  { kw: 'order crabs online', cat: 'product' },
                  { kw: 'live crab shipping', cat: 'product' },
                  { kw: 'crabplace', cat: 'brand' },
                  { kw: 'crabplace.com', cat: 'brand' },
                  { kw: 'crab place reviews', cat: 'brand' },
                  { kw: 'best place to buy crabs online', cat: 'product' },
                  { kw: 'maryland steamed crabs shipped', cat: 'product' },
                  { kw: 'blue crab delivery near me', cat: 'local' },
                  { kw: 'fresh seafood delivery', cat: 'product' },
                  { kw: 'live lobster delivery', cat: 'product' },
                  { kw: 'crab cakes shipped', cat: 'product' },
                  { kw: 'maryland crab soup', cat: 'product' },
                  { kw: 'old bay steamed crabs', cat: 'product' },
                  { kw: 'chesapeake bay crabs', cat: 'local' },
                  { kw: 'crisfield maryland crabs', cat: 'local' },
                  { kw: 'summer crab season', cat: 'seasonal' },
                  { kw: 'fathers day crab gift', cat: 'seasonal' },
                  { kw: 'christmas seafood delivery', cat: 'seasonal' },
                ].map(({ kw, cat }) => {
                  const exists = keywords.some(k => k.keyword.toLowerCase() === kw.toLowerCase());
                  return (
                    <button key={kw} onClick={async () => {
                      if (exists) return;
                      await neonQuery('seo_keyword_add', { keyword: kw, category: cat });
                      await loadData();
                    }}
                    style={{
                      padding: '4px 10px', borderRadius: 12, border: '1px solid var(--gray-200)',
                      background: exists ? 'var(--gray-100)' : 'white', cursor: exists ? 'default' : 'pointer',
                      fontSize: 11, fontWeight: 500, color: exists ? 'var(--gray-500)' : 'var(--navy)',
                      fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      {exists ? <Check size={11} style={{ color: '#1e8e3e' }} /> : <Plus size={11} />}
                      {kw}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Quick Start Guide */}
          <div className="card">
            <div className="card-header">How This Works</div>
            <div className="card-body" style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--gray-700)' }}>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>SEO Tracking:</p>
              <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                <li>Add keywords you want to rank for (use suggested starters or your own)</li>
                <li>Enter today's ranking position from Google Search Console (or manually search Google)</li>
                <li>Over time, the dashboard shows trends — which keywords are improving, declining, or stuck</li>
                <li>Once GSC is connected, this populates automatically every day</li>
              </ul>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>AEO Tracking:</p>
              <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                <li>Search your keywords on Google — does an AI Overview appear? Does it mention CrabPlace?</li>
                <li>Ask ChatGPT/Perplexity "Where can I buy live crabs online?" — does CrabPlace come up?</li>
                <li>Record results in the AEO tab to track visibility over time</li>
              </ul>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>AI Recommendations:</p>
              <ul style={{ paddingLeft: 20 }}>
                <li>Claude analyzes your keyword data and suggests improvements</li>
                <li>Recommendations are prioritized (Critical → Low) and categorized by type</li>
                <li>Mark items as done or dismiss them as you work through them</li>
                <li>Ask Claude in any chat: "Look at my SEO data and give me recommendations"</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
